<template>
    <div class="min-h-screen flex justify-center items-center bg-gray-50 py-12 px-12 sm:px-12 lg:px-20">
        <div class="w-full space-y-8">
            <div>
                <div class="flex items-center"><img alt="Workflow" class="mx-auto" src="/realguide.ico" draggable="false" /></div>
                <h2 class="mt-12 text-center text-3xl text-gray-900 font-normal">{{ $t("web-rg.signInToYourAccountRealguide") }}</h2>
            </div>
            <form action="#" class="mt-2">
                <input name="remember" type="hidden" value="true" />
                <div class="rounded-md shadow-sm -space-y-px">
                    <div>
                        <label class="sr-only c-color-black" for="email-address">{{ $t("shared.emailAddress") }}</label>
                        <InputText
                            id="email-address"
                            v-model="model.username"
                            class="mt-1 appearance-none rounded-none relative block w-full px-6 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm bg-white"
                            name="email"
                            v-bind:placeholder="$t('shared.emailAddress')"
                            required />
                        <!-- type="email" commented due to login err because we have usernames without @ -->
                    </div>
                    <div class="mt-1">
                        <label class="sr-only mt-1 c-color-black" for="password">{{ $t("shared.password") }}</label>
                        <InputText
                            id="password"
                            v-model="model.password"
                            autocomplete="current-password"
                            class="mt-1 appearance-none rounded-none relative block w-full px-6 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm bg-white"
                            name="password"
                            v-bind:placeholder="$t('shared.password')"
                            required
                            type="password" />
                    </div>
                </div>
                <div class="flex items-center justify-end">
                    <div class="text-sm">
                        <a class="font-medium text-indigo-600 hover:text-indigo-500" href="#">{{ $t("shared.forgotYourPassword") }}</a>
                    </div>
                </div>
                <div>
                    <Button
                        class="mt-2 group relative w-full flex justify-center py-2 px-12 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        v-bind:label="$t('shared.signIn')"
                        type="submit"
                        v-on:click.prevent="onLogin" />
                </div>
            </form>
        </div>
    </div>
</template>
<script lang="ts" setup>
import { useRouter } from "vue-router";
import { setupSession } from "@/util/SetupSession";
import { useWebsocketManager } from "@/plugins/WebRgManagerPlugin";
import { useClientInfo } from "@xlabmedical/medic-web-framework";
import { WebCADModule } from "@xlabmedical/medic-api";
import { onMounted, ref } from "vue";
import { UAParser } from "ua-parser-js";
import { GlobalConstants, isDevelopment } from "@/constants/GlobalConstants";

const buildDate = ref(new Date().toLocaleDateString());
const version = ref(isDevelopment() ? import.meta.env.VITE_GIT_SHA ?? "" : GlobalConstants.BUILD_NUMBER);
const deviceId = ref("");
const clientSignals = ref<Record<string, string>>({});

const model = ref({
    username: "",
    password: "",
});
const api = useServerApi();
const router = useRouter();
const auth = useAuth();
const sessionManager = useSessionManager();
const wsManager = useWebsocketManager();
const { collectClientSignals, generateVisitorId } = useClientInfo(() => UAParser());

onMounted(async () => {
    clientSignals.value = collectClientSignals();
    deviceId.value = await generateVisitorId(clientSignals.value);
});

const onLogin = async () => {
    if (model.value.username.length && model.value.password.length) {
        const success = await auth.login(model.value.username, model.value.password, WebCADModule.PLANNER, version.value, buildDate.value, deviceId.value);
        if (success) {
            await setupSession(auth, sessionManager, api, wsManager);
            await router.push("/patients");
        }
    }
};
</script>
<style scoped>
.p-inputtext {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
    font-size: 1rem;
    color: black !important;
    background: #eeeeee !important;
    padding: 0.429rem 0.571rem;
    transition:
        background-color 0.2s,
        color 0.2s,
        border-color 0.2s,
        box-shadow 0.2s;
    appearance: none;
    border-radius: 0.5rem;
}

body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
    font-size: 14px;
    color: black !important;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    padding: 0;
    margin: 0;
    min-height: 100vh;
    background: #28292c;
    background-repeat: no-repeat;
    background-attachment: fixed;
}
</style>
